import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSave, FiX } from 'react-icons/fi';

const CourseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [showCustomProgram, setShowCustomProgram] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    credits: 3,
    semester: 1,
    program: '',
    tahunAjaran: '',
    description: '',
    lecturer: '',
    schedule: {
      day: 'Senin',
      startTime: '',
      endTime: '',
      room: ''
    },
    isActive: true
  });

  const fetchCourse = async () => {
    try {
      const res = await axios.get(`/api/courses/${id}`);
      const courseData = res.data.data;
      setFormData(courseData);
      
      // Check if the course program is in the programs list
      // If not, enable custom input
      if (courseData.program && !programs.includes(courseData.program)) {
        setShowCustomProgram(true);
      }
    } catch (error) {
      toast.error('Gagal mengambil data mata kuliah');
      navigate('/courses');
    }
  };

  const fetchPrograms = async () => {
    try {
      const res = await axios.get('/api/courses');
      // Get unique programs from all courses
      const uniquePrograms = [...new Set(res.data.data.map(c => c.program))].filter(p => p);
      setPrograms(uniquePrograms.sort());
    } catch (error) {
      console.error('Failed to fetch programs');
      // Set default programs as fallback
      setPrograms([
        'S1 Ilmu Keperawatan',
        'S1 Kesehatan Masyarakat',
        'Profesi Profesi Ners',
        'Teknik Informatika',
        'Sistem Informasi'
      ]);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  useEffect(() => {
    if (id && programs.length > 0) {
      fetchCourse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, programs]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('schedule.')) {
      const scheduleField = name.split('.')[1];
      setFormData({
        ...formData,
        schedule: {
          ...formData.schedule,
          [scheduleField]: value
        }
      });
    } else {
      // Check if user selects "other" for program
      if (name === 'program') {
        if (value === '__custom__') {
          setShowCustomProgram(true);
          setFormData({
            ...formData,
            [name]: ''
          });
        } else {
          setShowCustomProgram(false);
          setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
          });
        }
      } else {
        setFormData({
          ...formData,
          [name]: type === 'checkbox' ? checked : value
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (id) {
        await axios.put(`/api/courses/${id}`, formData);
        toast.success('Mata kuliah berhasil diupdate');
      } else {
        await axios.post('/api/courses', formData);
        toast.success('Mata kuliah berhasil ditambahkan');
      }
      navigate('/courses');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Terjadi kesalahan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="student-form-page">
      <div className="page-header">
        <h2>{id ? 'Edit Mata Kuliah' : 'Tambah Mata Kuliah Baru'}</h2>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Informasi Mata Kuliah</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Kode Mata Kuliah *</label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  className="form-control"
                  required
                  placeholder="Contoh: TIF101"
                />
              </div>

              <div className="form-group">
                <label>SKS *</label>
                <input
                  type="number"
                  name="credits"
                  value={formData.credits}
                  onChange={handleChange}
                  className="form-control"
                  min="1"
                  max="6"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Nama Mata Kuliah *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Program Studi *</label>
                {showCustomProgram ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      name="program"
                      value={formData.program}
                      onChange={handleChange}
                      className="form-control"
                      required
                      placeholder="Masukkan nama program studi"
                    />
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        setShowCustomProgram(false);
                        setFormData({ ...formData, program: '' });
                      }}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Pilih dari List
                    </button>
                  </div>
                ) : (
                  <select
                    name="program"
                    value={formData.program}
                    onChange={handleChange}
                    className="form-control"
                    required
                  >
                    <option value="">Pilih Program Studi</option>
                    {programs.map(program => (
                      <option key={program} value={program}>{program}</option>
                    ))}
                    <option value="__custom__">+ Input Manual</option>
                  </select>
                )}
              </div>

              <div className="form-group">
                <label>Semester *</label>
                <input
                  type="number"
                  name="semester"
                  value={formData.semester}
                  onChange={handleChange}
                  className="form-control"
                  min="1"
                  max="8"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Tahun Ajaran *</label>
              <input
                type="text"
                name="tahunAjaran"
                value={formData.tahunAjaran}
                onChange={handleChange}
                className="form-control"
                required
                placeholder="Contoh: 2024/2025"
              />
            </div>

            <div className="form-group">
              <label>Nama Dosen *</label>
              <input
                type="text"
                name="lecturer"
                value={formData.lecturer}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label>Deskripsi</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-control"
                rows="3"
              ></textarea>
            </div>
          </div>

          <div className="form-section" style={{ marginTop: '30px' }}>
            <h3>Jadwal Kuliah</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Hari</label>
                <select
                  name="schedule.day"
                  value={formData.schedule.day}
                  onChange={handleChange}
                  className="form-control"
                >
                  <option value="Senin">Senin</option>
                  <option value="Selasa">Selasa</option>
                  <option value="Rabu">Rabu</option>
                  <option value="Kamis">Kamis</option>
                  <option value="Jumat">Jumat</option>
                  <option value="Sabtu">Sabtu</option>
                </select>
              </div>

              <div className="form-group">
                <label>Ruangan</label>
                <input
                  type="text"
                  name="schedule.room"
                  value={formData.schedule.room}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Contoh: R101"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Jam Mulai</label>
                <input
                  type="time"
                  name="schedule.startTime"
                  value={formData.schedule.startTime}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Jam Selesai</label>
                <input
                  type="time"
                  name="schedule.endTime"
                  value={formData.schedule.endTime}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  style={{ marginRight: '8px' }}
                />
                Mata kuliah aktif
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/courses')}
            >
              <FiX /> Batal
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              <FiSave /> {loading ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseForm;
